import React, { useEffect, useState } from 'react';
import { Bell, BellOff, Loader2, X, CheckCircle2 } from 'lucide-react';
import {
  isPushSupported,
  getPushStatus,
  enablePushNotifications,
  disablePushNotifications,
} from '../utils/webPush';

/**
 * Reusable push toggle card — shows current subscription status and
 * lets the user opt in / out. Suitable for user & agent profile screens.
 *
 * Props:
 *   user_id, user_type — optional; stamped on the subscription row so
 *     admins can target segmented campaigns. Safe to omit for guests.
 *   compact — if true, render the inline-button version (no card chrome).
 */
const PushToggleCard = ({ user_id = null, user_type = 'guest', compact = false }) => {
  const [status, setStatus] = useState({ supported: false, permission: 'default', subscribed: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const refresh = async () => {
    const s = await getPushStatus();
    setStatus(s);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const toggle = async () => {
    setBusy(true);
    setErr('');
    try {
      if (status.subscribed) {
        await disablePushNotifications();
      } else {
        await enablePushNotifications({ user_id, user_type });
      }
      await refresh();
    } catch (e) {
      setErr(e.message || 'Unable to change push preference');
    }
    setBusy(false);
  };

  if (!isPushSupported()) {
    if (compact) return null;
    return (
      <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 flex items-start gap-3" data-testid="push-unsupported-card">
        <BellOff className="w-5 h-5 text-gray-400 mt-0.5" />
        <div className="text-sm text-gray-600">
          Push notifications are not supported in this browser.
        </div>
      </div>
    );
  }

  if (loading) {
    return compact ? null : (
      <div className="p-4 rounded-xl border border-gray-200 bg-white flex items-center gap-3">
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        <span className="text-sm text-gray-500">Checking push status…</span>
      </div>
    );
  }

  const denied = status.permission === 'denied';

  if (compact) {
    return (
      <button
        data-testid="push-toggle-compact"
        onClick={toggle}
        disabled={busy || denied}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
          status.subscribed
            ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
            : 'bg-orange-500 hover:bg-orange-600 text-white'
        } disabled:opacity-50`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : status.subscribed ? <CheckCircle2 className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
        {status.subscribed ? 'Notifications On' : denied ? 'Blocked in browser' : 'Enable Notifications'}
      </button>
    );
  }

  return (
    <div
      data-testid="push-toggle-card"
      className="p-4 rounded-xl border border-gray-200 bg-white flex items-start gap-3"
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
        status.subscribed ? 'bg-emerald-100 text-emerald-600' : 'bg-orange-100 text-orange-600'
      }`}>
        {status.subscribed ? <CheckCircle2 className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-gray-900">Push Notifications</h4>
        <p className="text-xs text-gray-500 mt-1">
          {status.subscribed
            ? 'You\'ll hear from us about price drops, booking confirmations, and deals on your favourite sectors.'
            : denied
              ? 'You\'ve blocked notifications for this site. Enable them in your browser settings to opt in.'
              : 'Get instant alerts on price drops & booking confirmations.'}
        </p>
        {err && <p className="text-xs text-red-600 mt-2">{err}</p>}
      </div>
      <button
        data-testid="push-toggle-btn"
        onClick={toggle}
        disabled={busy || denied}
        className={`px-4 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition ${
          status.subscribed
            ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            : 'bg-orange-500 hover:bg-orange-600 text-white'
        } disabled:opacity-50`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : status.subscribed ? 'Disable' : 'Enable'}
      </button>
    </div>
  );
};

/**
 * Floating prompt that appears once (per localStorage flag) for logged-in
 * users who haven't subscribed yet. Tap "Enable" to opt in, X to dismiss.
 */
export const PushOptInBanner = ({ user_id = null, user_type = 'guest' }) => {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const FLAG_KEY = 'acf_push_prompt_dismissed_at';

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isPushSupported()) return;
      const s = await getPushStatus();
      if (!alive) return;
      // Don't re-prompt if already subscribed, denied, or dismissed within last 7 days
      if (s.subscribed || s.permission === 'denied') return;
      try {
        const dismissed = localStorage.getItem(FLAG_KEY);
        if (dismissed) {
          const ageDays = (Date.now() - parseInt(dismissed, 10)) / 86400000;
          if (ageDays < 7) return;
        }
      } catch (_) { /* ignore */ }
      setShow(true);
    })();
    return () => { alive = false; };
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await enablePushNotifications({ user_id, user_type });
      setShow(false);
    } catch (_) {
      // swallow — user sees the browser prompt decline
    }
    setBusy(false);
  };

  const dismiss = () => {
    try { localStorage.setItem(FLAG_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="push-optin-banner"
      className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:w-96 bg-white border border-gray-200 shadow-xl rounded-2xl p-4 z-50 flex items-start gap-3"
    >
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center flex-shrink-0">
        <Bell className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-gray-900 text-sm">Stay in the loop</h4>
        <p className="text-xs text-gray-600 mt-0.5">Enable push to get price drop alerts & booking confirmations instantly.</p>
        <div className="flex gap-2 mt-2">
          <button
            data-testid="push-optin-enable"
            onClick={enable}
            disabled={busy}
            className="bg-orange-500 hover:bg-orange-600 text-white text-xs px-3 py-1.5 rounded-md font-medium disabled:opacity-50 flex items-center gap-1"
          >
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}
            Enable
          </button>
          <button
            data-testid="push-optin-dismiss"
            onClick={dismiss}
            className="text-gray-500 hover:text-gray-700 text-xs px-3 py-1.5 rounded-md font-medium"
          >
            Not now
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="p-1 text-gray-400 hover:text-gray-600"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default PushToggleCard;
